import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError, setApiScopeOrg } from '@/api/client';

export interface AuthUser {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'sales';
  is_super_admin: boolean;
  organization_id: string;
}

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'unauthenticated' };

interface Ctx {
  state: AuthState;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = React.createContext<Ctx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const [state, setState] = React.useState<AuthState>({ status: 'loading' });
  // Track the currently authenticated user id so we can detect tenant
  // switches across login/logout and wipe React Query cache when the
  // identity changes (otherwise the prior session's data bleeds into
  // the new org's UI -- a real cross-tenant leak in single-page apps).
  const previousUserId = React.useRef<string | null>(null);

  function applyAuthedUser(u: AuthUser) {
    if (previousUserId.current && previousUserId.current !== u.id) {
      // Different identity than before -- wipe everything cached under
      // the old session before mounting the new user's UI.
      qc.clear();
    }
    previousUserId.current = u.id;
    // New session never inherits a stale super-admin impersonation.
    setApiScopeOrg(null);
    setState({ status: 'authenticated', user: u });
  }

  const refresh = React.useCallback(async () => {
    try {
      const u = await api.get<AuthUser>('/api/auth/me');
      applyAuthedUser(u);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        if (previousUserId.current) qc.clear();
        previousUserId.current = null;
        setApiScopeOrg(null);
        setState({ status: 'unauthenticated' });
        return;
      }
      setState({ status: 'unauthenticated' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc]);

  React.useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = React.useCallback(async (email: string, password: string) => {
    const u = await api.post<AuthUser>('/api/auth/login', { email, password });
    applyAuthedUser(u);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qc]);

  const logout = React.useCallback(async () => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      /* ignore */
    }
    qc.clear();
    previousUserId.current = null;
    setApiScopeOrg(null);
    setState({ status: 'unauthenticated' });
  }, [qc]);

  return <AuthContext.Provider value={{ state, login, logout, refresh }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
